import styled from "styled-components";
import {
  TypographyProps,
  typography,
  BackgroundColorProps,
} from "styled-system";
import { VStack, HStack, Text } from "@bpba/ui-components";
import { PreguntaData } from "../../model/preguntas/PreguntaData";
import { HorizontalRule } from "../HorizontalRule";
import { ArrowContainer } from "./ArrowContainer";
import { Feedback } from "./Feedback";
import { ResponseContainer } from "./ResponseContainer";
import { FeedbackStorageManager } from "./FeedbackStorageManager";
import { Video } from "./Video";
import { useFeedbackSender } from "./useFeedbackSender";
import { FeedbackTipo } from "../../model/preguntas/FeedbackTipo";
import { FAQ_TYPE } from "../../constants/data";

type FaqProps = {
  item: PreguntaData;
  feedbackStorage: FeedbackStorageManager;
  isOpen: boolean;
  setOpen: (id: string | undefined) => void;
  index: number;
} & BackgroundColorProps;

export function Faq(props: FaqProps) {
  const { item, feedbackStorage, isOpen, setOpen, index, backgroundColor } =
    props;
  const [send] = useFeedbackSender(handleValidResponse, "Faq");

  /** Notifica al backend que la respuesta fue vista por el usuario */
  async function notifyView() {
    await send({ idBtn: item.id, rate: FeedbackTipo.Vista });
  }

  function handleValidResponse() {}

  async function clickTitle() {
    setOpen(isOpen ? undefined : item.id);

    if (!isOpen) await notifyView();
  }

  async function onKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === "Enter") {
      await clickTitle();
      return;
    }
  }

  function getAriaLabelQuestion(): string {
    return `${FAQ_TYPE} ${index}: ${item?.pregunta}`;
  }

  const responseTabIndex: number = isOpen ? 0 : -1;

  if (
    !item?.tienePregunta() ||
    (!item?.tieneRespuesta() && !item?.tieneVideo())
  )
    return <></>;

  return (
    <VStack>
      <ClickableHStack
        justifyContent={"space-between"}
        alignItems="center"
        paddingTop={[28, 36]}
        paddingBottom={[16, 14]}
        onClick={clickTitle}
        onKeyDown={onKeyDown}
      >
        <StyledText
          fontSize={[14, 16]}
          lineHeight={["18px", "21px"]}
          fontWeight={isOpen ? 700 : 400}
          marginY="0px"
          aria-label={getAriaLabelQuestion()}
          role="link"
          tabIndex={0}
        >
          {item.pregunta}
        </StyledText>
        <ArrowContainer
          isUp={isOpen}
          height={14}
          width={14}
          title={isOpen ? "Ocultar respuesta" : "Mostrar respuesta"}
          tabIndex={0}
        />
      </ClickableHStack>
      <DropdownVStack
        height={isOpen ? "auto" : "0px"}
        overflow="hidden"
        marginTop={isOpen ? "24px" : "0px"}
        marginBottom={isOpen ? "18px" : "0px"}
        fontSize={[12, 16]}
        lineHeight={["15px", "20px"]}
      >
        <ResponseContainer
          respuesta={item.respuesta}
          tabIndex={responseTabIndex}
        />
        <Video
          videoUrl={item.video}
          backgroundColor={backgroundColor}
          tabIndex={responseTabIndex}
        />
        <Feedback
          preguntaData={item}
          feedbackStorage={feedbackStorage}
          tabIndex={responseTabIndex}
        />
      </DropdownVStack>
      <HorizontalRule marginBottom="0px" marginTop="0px" />
    </VStack>
  );
}

const ClickableHStack = styled(HStack)({
  cursor: "pointer",
});

const DropdownVStack = styled(VStack)<TypographyProps>(typography, {
  transition: "all 0.5s ease",
  "-webkit-transition": "all 0.5s ease",
  "-moz-transition": "all 0.5s ease",
});

const StyledText = styled(Text)({
  outlineWidth: "0px",
});
